import { useMemo, useState, useCallback } from 'react';
import * as Apollo from '@apollo/client';
import { DocumentNode } from 'graphql';
import {
  TViewableQuery,
  TViewableQueryVariables,
  ViewableQueryHookResult,
} from './viewable.generated';
import { ViewableType } from 'types/types';
import { normalizeWatchViewViewable } from './normalizeViewable';

/*
* @example
* const { data, loading, error } = useViewable({
  *   variables: {
    *      viewableId: string
    *      broadcastId: string
    *   },
  * });
*/

type NormalizedResult = {
  data: ViewableType
};

type ViewableResult = Omit<ViewableQueryHookResult, 'data'> & NormalizedResult;

export default function useViewable(
  query: DocumentNode,
  options: Apollo.QueryHookOptions<TViewableQuery, TViewableQueryVariables>,
): ViewableResult {
  const [counter, setCounter] = useState(0);
  const { data, refetch, ...res } = Apollo.useQuery(query, options);

  const viewable = useMemo(
    () => {
      if (data?.viewer?.viewable) {
        return normalizeWatchViewViewable(data.viewer.viewable, options.variables?.broadcastId);
      }
      return undefined;
    },
    [data?.viewer?.viewable, counter],
  );

  // refresh viewable after refetch
  const refetchHandler = useCallback(() => refetch && refetch()?.then((result) => {
    if (result.data === data) setCounter(counter + 1);
    return result;
  }), [refetch, counter]);

  return {
    data: viewable,
    refetch: refetchHandler,
    ...res,
  };
}
